import { useIonRouter } from "@ionic/react";
import { useLocalize } from "@france/superelements/utils";
import { lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "react-redux";
import type { RootState } from "../../../redux";
import useClearProposal from "../../../redux/slices/proposal/hooks/useClearProposal";

import ROUTES from "../../../router/constants/routes";
import {
  PANE_INDEX,
  PANE_INDEX_REV,
  ProposalCreationPanes,
} from "../types/panes.types";
import useProposal from "../../../redux/slices/proposal/hooks/useProposal";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import useAppSelector from "../../../redux/hooks/useAppSelector";
import { SetCurrentStep } from "../../../redux/slices/proposal";

/* put them in order pls */
export const createProposalPanePaths = [
  ROUTES.SELECT_POS,
  ROUTES.RECOMMENDED_ASSORTMENT,
  ROUTES.COMMERCIAL_PROPOSAL,
  ROUTES.RECAP,
];

const useProposalNavigation = (
  defaultPane?: ProposalCreationPanes,
  onLastPane?: () => void,
) => {
  // Selectors & Dispatch
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const currentStepNumber = useAppSelector(
    (state) => state.proposal.currentStep,
  );
  const maxStep = useAppSelector((state) => state.proposal.maxStep);

  // Refs
  const returnRoute = useRef("/");

  const paneOne = useRef<HTMLDivElement>(null);
  const paneTwo = useRef<HTMLDivElement>(null);
  const paneThree = useRef<HTMLDivElement>(null);
  const paneFour = useRef<HTMLDivElement>(null);

  const PanesComponent = useRef({
    [ProposalCreationPanes.POS]: lazy(
      () => import("../panes/ProposalInformation/ProposalInformation"),
    ),
    [ProposalCreationPanes.PRODUCTS]: lazy(
      () => import("../panes/EditAssortment/EditAssortment"),
    ),
    [ProposalCreationPanes.CC]: lazy(
      () => import("../panes/CommercialProposal/CommercialProposal"),
    ),
    [ProposalCreationPanes.RECAP]: lazy(() => import("../panes/Recap/Recap")),
  });

  // Hooks
  const t = useLocalize();
  const { push, routeInfo } = useIonRouter();
  const clearProposalState = useClearProposal();
  const { createProposal } = useProposal();

  // States
  const [isCreationError, setCreationError] = useState(false);

  // Memos
  const currentPane = useMemo(
    () => PANE_INDEX_REV[currentStepNumber],
    [currentStepNumber],
  );

  const paneNames = useMemo(
    () => [
      t("pos"),
      t("assortment"),
      t("commercial_conditions_short"),
      t("proposal_recap_short"),
    ],
    [t],
  );

  const panes = useMemo(
    () => [paneOne, paneTwo, paneThree, paneFour],
    [paneOne, paneTwo, paneThree, paneFour],
  );

  // Handlers
  const onHeaderCrossBtnClick = useCallback(() => {
    push(returnRoute.current);
    clearProposalState();
  }, [push, clearProposalState]);

  const goHome = useCallback(() => {
    push(ROUTES.HOME);
    clearProposalState();
  }, [push, clearProposalState]);

  const onPaneSelect = useCallback(
    async ({
      direction,
      pane,
    }: {
      direction?: 1 | -1;
      pane?: ProposalCreationPanes;
    }) => {
      let nextPane = pane ? PANE_INDEX[pane] : 0;
      if (direction) {
        nextPane = currentStepNumber + direction;
      }

      // Read at call time: a pane may call this right after loading a draft.
      if (nextPane >= 1 && !store.getState().proposal.proposalId) {
        const id = await createProposal();
        if (!id) {
          setCreationError(true);
          return;
        }
      }
      if (nextPane === PANE_INDEX[ProposalCreationPanes.RECAP]) {
        // Create Slides (if possible only when there was changes)
        onLastPane?.();
      }

      // Move to next Pane
      dispatch(SetCurrentStep(nextPane));
      // push(createProposalPanePaths[nextPane]);
    },
    [currentStepNumber, dispatch, store, onLastPane, createProposal],
  );

  const closeCreationError = useCallback(() => setCreationError(false), []);

  const retryCreation = useCallback(() => {
    setCreationError(false);
    onPaneSelect({ pane: ProposalCreationPanes.PRODUCTS });
  }, [onPaneSelect]);

  // Effects
  useEffect(() => {
    if (
      !routeInfo.pushedByRoute?.includes(ROUTES.CREATE_PROPOSAL) &&
      routeInfo.pushedByRoute
    )
      returnRoute.current = routeInfo.pushedByRoute;
  }, [routeInfo.pushedByRoute]);

  useEffect(() => {
    push(createProposalPanePaths[currentStepNumber], "none", "replace");
    // eslint-disable-next-line
  }, [currentStepNumber]);

  return {
    panes,
    maxStep,
    PanesComponent,
    returnRoute,
    paneNames,
    currentPane,
    goHome,
    onPaneSelect,
    onHeaderCrossBtnClick,

    isCreationError,
    closeCreationError,
    retryCreation,

    scrollable: {
      paneOne,
      paneTwo,
      paneThree,
      paneFour,
    },
  };
};

export default useProposalNavigation;
